<script setup>
/* eslint semi: 1 */
import { computed, h } from 'vue';
import { createColumnHelper, FlexRender } from '@tanstack/vue-table';
import { useI18n } from 'vue-i18n';
import { DateTime, Duration } from 'luxon';
import Draggable from 'vuedraggable';
import MediaItemPreview from '@/components/common/media/media-item-preview.vue';
import { useInlineNameEdit } from '@/composables/media/use-inline-name-edit';
import { useMediaStore } from '@/pinia/stores/media/mediaStore';

const columnHelper = createColumnHelper();
const { t } = useI18n();
const props = defineProps({
  items: {
    type: Array,
    default: () => [],
  },
  showLoader: {
    type: Boolean,
    default: false,
  },
  orientation: {
    type: String,
    default: 'horizontal',
    validator: (v) => ['horizontal', 'vertical'].includes(v),
  },
  isAudioPicker: {
    type: Boolean,
    default: false,
  },
  currentAudioPlaying: {
    type: Object,
    default: () => ({
      isPlaying: false,
      audio: null
    }),
  },
  hasSelection: {
    type: Boolean,
    default: false,
  },
  selectedIds: {
    type: Set,
    default: () => new Set(),
  },
  enableDelete: {
    type: Boolean,
    default: false,
  },
  enableCopy: {
    type: Boolean,
    default: false,
  },
  enableEditProject: {
    type: Boolean,
    default: false,
  },
  /**
   * When set, switches the grid into LED/Videowall mode where each card is
   * sized by JS-computed pixel dimensions derived from the resolution's aspect
   * ratio rather than CSS fractional widths.
   * Shape: { width: number, height: number }
   */
  ledTag: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits([
  'card-click',
  'delete-click',
  'copy-click',
  'edit-click',
  'video-play',
  'image-preview',
  'audio-play',
  'audio-pause',
  'svg-load',
  'iframe-mouseenter',
  'iframe-mouseleave',
  'drag-tag-to-media',
  'edit-name'
]);

/**
 * Hook function for Draggable as a drop zone for the `tags-shared` group.
 * Mirrors the same handler in media-grid-list.vue.
 * @param {Object} event - VueDraggable add event
 * @param {Object} media - Media item for the row that received the drop
 */
function handleTagDraggableAdd (event, media) {
  emit('drag-tag-to-media', { event, media });
}

const mediaStore = useMediaStore();
const { editingResourceId, startEditNameMode, confirmEditName } = useInlineNameEdit();

const columns = [
  columnHelper.accessor((row) => row.preview?.url ?? '', {
    id: 'preview',
    enableSorting: false,
    size: 160,
    minSize: 160,
    maxSize: 160,
    meta: {
      width: '160px',
      minWidth: '160px'
    },
    header: () => '',
    cell: (info) => {
      if (!info?.row?.original || !info.getValue) {
        return h('span', {}, '');
      }

      return h('div', {
        class: [
          'group',
          previewDimClasses.value,
          info.row.original.disabled ? 'grayscale opacity-60' : '',
        ]
      }, [
        h(MediaItemPreview, {
          item: info.row.original,
          isSelected: props.hasSelection && props.selectedIds.has(info.row.original.resourceId),
          isAudioPlaying: props.currentAudioPlaying?.isPlaying,
          isCurrentAudioPlaying: props.currentAudioPlaying?.isPlaying && props.currentAudioPlaying?.audio?.resourceId === info.row.original.resourceId,
          orientation: props.orientation,
          isMini: true,
          bezelsMask: resolvedBezelsMask.value,
          onVideoPlay: ($event) => emit('video-play', $event),
          onImagePreview: ($event) => emit('image-preview', $event),
          onAudioPlay: ($event) => emit('audio-play', $event),
          onAudioPause: ($event) => emit('audio-pause', $event),
          onSvgLoad: ($event) => emit('svg-load', $event)
        }),
        h('div', {
          class: 'z-[1] box-border absolute inset-0 group-hover:bg-white group-hover:opacity-50',
          onMouseenter: () => emit('iframe-mouseenter', info.row.original),
          onMouseleave: () => emit('iframe-mouseleave', info.row.original)
        })
      ]);
    }
  }),
  columnHelper.accessor('name', {
    id: 'name',
    enableSorting: false,
    size: 420,
    minSize: 420,
    maxSize: 420,
    meta: {
      width: '420px',
      minWidth: '420px',
      colMaxWidth: '420px'
    },
    header: () => t('label.name'),
    cell: (info) => {
      if (!info?.row?.original || !info.getValue) {
        return h('span', {}, '');
      }

      const item = info.row.original;
      const resourceId = item.resourceId;

      if (editingResourceId.value === resourceId) {
        return h('input', {
          class: 'w-full bg-transparent border-none outline-none text-xs',
          value: item.name,
          onVnodeMounted: (vnode) => vnode.el?.focus(),
          onBlur: handleConfirmEditName,
          onKeydown: handleConfirmEditName,
        });
      }

      return h('span', {
        class: 'cursor-text',
        onClick: (e) => { e.stopPropagation(); startEditNameMode(resourceId); },
      }, mediaStore.optimisticNames[resourceId] ?? info.getValue());
    }
  }),
  columnHelper.accessor('type', {
    id: 'type',
    enableSorting: false,
    header: () => t('label.fileType'),
    cell: (info) => {
      if (!info?.row?.original || !info.getValue) {
        return h('span', {}, '');
      }

      return h('span', { class: 'font-semibold uppercase text-gray-900' }, info.getValue());
    }
  }),
  columnHelper.accessor('modifiedAt', {
    id: 'modifiedAt',
    enableSorting: false,
    header: () => t('label.lastModified'),
    cell: (info) => {
      if (!info?.row?.original || !info.getValue) {
        return h('span', {}, '');
      }
      const creationDate = DateTime.fromISO(info.getValue()).toLocaleString(DateTime.DATETIME_MED);
      return h('span', {}, creationDate);
    }
  }),
  columnHelper.accessor('dur', {
    id: 'dur',
    enableSorting: false,
    header: () => t('label.duration'),
    cell: (info) => {
      const duration = info?.getValue?.() ?? info?.row?.original?.dur ?? info?.row?.original?.mediaDuration;
      if (typeof duration !== 'number') {
        return h('span', {}, '-');
      }

      const durationTime = Duration.fromObject({ seconds: duration }).toFormat('hh:mm:ss');
      return h('span', {}, durationTime);
    }
  }),
  columnHelper.display({
    id: 'actions',
    enableSorting: false,
    size: 84,
    minSize: 84,
    maxSize: 84,
    meta: {
      width: '84px'
    },
    header: () => '',
    cell: (info) => {
      const isSelected = props.selectedIds?.has(info.row?.original?.resourceId) ?? false;

      return h('div', { class: ['flex items-center gap-2', !isSelected ? 'invisible' : ''] }, [
        // Copy Project Media icon
        ...(props.enableCopy && ['svg', 'html'].includes(info?.row?.original?.preview?.type) && !info?.row?.original?.status && info?.row?.original?.type !== 'audio' && props.selectedIds.size <= 1
          ? [
              h('div', {
                class: ['media-item-action-icon', 'copy'],
                onClick: (e) => { e.stopPropagation(); emit('copy-click', info.row.original); },
              }, [h('div', { class: 'icon' })]),
            ]
          : []),
        // Edit Project Media icon
        ...(props.enableEditProject && ['svg', 'html'].includes(info?.row?.original?.preview?.type) && !info?.row?.original?.status && info?.row?.original?.type !== 'audio' && props.selectedIds.size === 1
          ? [
              h('div', {
                class: ['media-item-action-icon', 'edit'],
                onClick: (e) => { e.stopPropagation(); emit('edit-click', info.row.original); },
              }, [h('div', { class: 'icon' })]),
            ]
          : []),
        // Media Card Delete icon (only when user has delete rights)
        ...(props.enableDelete
          ? [
              h('div', {
                class: ['media-item-action-icon', 'delete'],
                onClick: (e) => { e.stopPropagation(); emit('delete-click', info.row.original); },
              }, [h('div', { class: 'icon' })]),
            ]
          : []),
      ]);
    }
  })
];

// In audio-picker mode the layout is always horizontal regardless of `orientation`.
const isVertical = computed(() => props.orientation === 'vertical' && !props.isAudioPicker);

// The preview cell uses Tailwind's w-20 (5rem = 80px) as its fixed width.
// cardHeight is derived from the same aspect ratios used in previewDimClasses
// so that bezel overlays scale correctly to the rendered preview box.
const PREVIEW_CARD_WIDTH = 80; // w-20 = 5rem = 80px

const tablePreviewCardDimensions = computed(() => {
  const cardWidth = PREVIEW_CARD_WIDTH;
  const cardHeight = isVertical.value
    ? cardWidth * (97 / 55)   // portrait  aspect-[55/97]
    : cardWidth * (55 / 97);  // landscape aspect-[97/55]
  return { cardWidth, cardHeight };
});

/**
 * Computed bezels mask from the selected LED tag and the
 * fixed table preview dimensions, passed to
 * MediaItemPreview for bezel overlay rendering.
 *
 * The cell box is w-20 × aspect-ratio-derived height.
 * The LED content is fitted inside that box using contain logic
 * (same as object-fit: contain) so cardWidth/cardHeight reflect
 * the actual rendered content area, not the full cell.
 */
const resolvedBezelsMask = computed(() => {
  if (!props.ledTag) return null;

  const { cardWidth: cellWidth, cardHeight: cellHeight } = tablePreviewCardDimensions.value;
  const { width: srcWidth, height: srcHeight } = props.ledTag;

  // Contain-fit the LED resolution into the cell box
  const srcAR = srcWidth / srcHeight;
  const cellAR = cellWidth / cellHeight;

  let contentWidth, contentHeight;
  if (srcAR > cellAR) {
    // LED is wider relative to cell → constrained by cell width
    contentWidth = cellWidth;
    contentHeight = cellWidth / srcAR;
  } else {
    // LED is taller relative to cell → constrained by cell height
    contentHeight = cellHeight;
    contentWidth = cellHeight * srcAR;
  }

  return {
    bezels: props.ledTag.bezels,
    resolutionWidth: srcWidth,
    resolutionHeight: srcHeight,
    cardWidth: contentWidth,
    cardHeight: contentHeight,
  };
});

// Preview container dimensions.
// Audio-picker: fixed-height full-width row (the column width is the whole container).
// Grid: fills the grid column width; height is derived from the original aspect ratios
//   (97×55 landscape / 55×97 portrait from .media-preview-fixed in media-list.scss)
//   so width and height always scale together as the viewport changes.
const previewDimClasses = computed(() => {
  const baseClasses = [
    'relative overflow-hidden w-20 m-2 max-w-full bg-cover bg-[center_50%] rounded-sm shadow-[0_0_1px_rgba(0,0,0,0.8)] checkboard-background'
  ];

  if (props.isAudioPicker) {
    baseClasses.push('h-[42px] min-[1920px]:h-[70px]');
  }

  if (isVertical.value) {
    baseClasses.push('aspect-[55/97]');   // portrait  ≈ 55 : 97
  } else {
    baseClasses.push('aspect-[97/55]');  // landscape ≈ 97 : 55
  }

  return baseClasses.join(' ');
});

/**
 * Handles the blur event from the inline name input and the Enter keydown for accessibility.
 * Calls confirmEditName to get the rename payload, then emits 'edit-name'
 * so the parent can call the API and revert via mediaStore.clearOptimisticName on failure.
 * @param {FocusEvent|KeyboardEvent} event
 */
function handleConfirmEditName (event) {
  if (event.type === 'keydown') {
    if (event.key !== 'Enter') return;
    event.target.blur(); // blur re-triggers this handler to confirm
    return;
  }

  const result = confirmEditName(event);
  if (!result) return;
  emit('edit-name', result);
}
</script>

<template>
  <BaseDataTable
    ref="dataTableRef"
    class="media-table-list-wrapper"
    :data="items"
    :columns="columns"
    :show-init-table-loader="showLoader"
    :empty-placeholder="$t('label.noSearchResults')"
    :empty-filtered-placeholder="$t('label.noSearchResults')"
    global-filter-type="manual"
    sort-type="client-side"
    pagination-type="load-more"
    disable-height-auto-calculation
  >
    <template #body-row="{ row, index }">
      <Draggable
        class="media-item-row border-b border-gray-200"
        :class="hasSelection && selectedIds?.has(row.original.resourceId) ? 'bg-gray-100' : 'bg-white'"
        tag="tr"
        item-key="id"
        ghost-class="sortable-ghost"
        :id="row.original.resourceId"
        :list="row.getVisibleCells()"
        :sort="false"
        :group="{ name: 'tags-shared', pull: false, put: true }"
        :data-component-element-id="`data-table-body-row-${index}`"
        @add="handleTagDraggableAdd($event, row.original)"
      >
        <template #item="{ element: cell }">
          <td
            v-if="typeof cell?.getContext === 'function'"
            :key="cell.id"
            :data-component-element-id="`data-table-body-row-${index}-cell-${cell.id}`"
            class="h-[54px] px-4 text-xs truncate text-ellipsis"
            :style="{
              'max-width': (cell.column.columnDef?.meta)?.colMaxWidth ?? 'unset',
            }"
            @click="$emit('card-click', cell.row.original)"
          >
            <FlexRender
              :render="cell?.column?.columnDef?.cell"
              :props="cell.getContext()"
            />
          </td>
          <td v-else class="hidden h-0 w-0 p-0 border-none" />
        </template>
      </Draggable>
    </template>
  </BaseDataTable>
</template>

<style lang="scss" scoped>
@reference '#tailwind.scss';

.media-table-list-wrapper {
  @apply h-full!;

  :deep(.data-table-container) {
    @apply max-h-full! h-full;

    .media-item-action-icon {
      & {
        @apply w-6 h-6 p-0 opacity-40 hover:opacity-100 cursor-pointer;
      }

      .icon {
        @apply w-full h-full p-0 bg-no-repeat bg-center border-[1px_solid_transparent];
        background-size: contain !important;
      }

      &.delete {
        .icon {
          background: url('../../../static/icons/remove.svg') no-repeat center center;
        }
      }

      &.copy {
        .icon {
          background: url('../../../static/icons/copy.svg') no-repeat center center;
        }
      }

      &.edit {
        .icon {
          background: url('../../../static/icons/edit.svg') no-repeat center center;
        }
      }
    }

    .data-table-body-row-empty-placeholder > td {
      @apply uppercase;
    }

    .media-item-row {
      &.ui-droppable-hover {
        & {
          @apply opacity-80! box-border!;
          background: url('../../../static/images/gradient-background-md.png') no-repeat !important;
          background-size: cover !important;
        }
      }

      .sortable-ghost {
        @apply hidden!;
      }
    }
  }

  .checkboard-background {
    background-image:
      linear-gradient(45deg, #dddddd 25%, transparent 25%),
      linear-gradient(-45deg, #dddddd 25%, transparent 25%),
      linear-gradient(45deg, transparent 75%, #dddddd 75%),
      linear-gradient(-45deg, transparent 75%, #dddddd 75%);
    background-size: 14px 14px !important;
    background-position: 0 0, 0 7px, 7px -7px, -7px 0px !important;
  }
}
</style>
